import manifest from './baseballDiceManifest.json';
import type { TableDie } from './raceDice.ts';

export interface BaseballDiceLayer {
  die: TableDie;
  movie: string;
  still: string;
  sound: string;
  bounds: readonly number[];
  depth: readonly number[];
  tint: readonly number[];
  stain: number;
}

const TINT: Record<string, readonly number[]> = {
  metallic: [0.74, 0.76, 0.78],
  white: [1.06, 1.02, 0.90],
  black: [0.25, 0.27, 0.27],
  red: [1.12, 0.62, 0.48],
  blue: [0.52, 0.78, 1.16],
};

export function baseballDiceRecipe(dice: readonly TableDie[], variation: number): BaseballDiceLayer[] | null {
  if (dice.length < 1 || dice.length > 3 || dice.some((die) => die.sides !== 6)) return null;
  const layouts = manifest.layouts[String(dice.length) as keyof typeof manifest.layouts] as readonly (readonly number[])[];
  const layout = layouts[variation % layouts.length];
  return dice.map((die, index) => {
    const path = layout[index];
    const prefix = `/baseball-dice-film/natural/path-${String(path).padStart(2, '0')}/face-${die.face}`;
    return {
      die,
      movie: `${prefix}.mp4`,
      still: `${prefix}.webp`,
      sound: `/baseball-dice-film/sound/path-${String(path).padStart(2, '0')}.mp3`,
      bounds: manifest.paths[path].bounds,
      depth: manifest.paths[path].depth,
      tint: TINT[die.role] ?? TINT.metallic,
      stain: die.role === 'white' ? 0.25 : 1,
    };
  });
}

export function createBaseballDiceCompositor(canvas: HTMLCanvasElement, layers: readonly BaseballDiceLayer[], plate: HTMLImageElement) {
  const gl = canvas.getContext('webgl', { alpha: false, antialias: false, preserveDrawingBuffer: false });
  if (!gl) throw new Error('Video compositing unavailable');
  const vertex = 'attribute vec2 p; uniform vec4 bounds; varying vec2 uv; void main(){uv=p; vec2 xy=bounds.xy+p*bounds.zw; gl_Position=vec4(xy.x*2.-1.,1.-xy.y*2.,0.,1.);}';
  const fragment = `precision mediump float; varying vec2 uv; uniform sampler2D film; uniform float pass; uniform float frame; uniform vec3 tint; uniform float stain;
  void main(){vec3 rgb; float a=1.; if(pass<0.)rgb=texture2D(film,uv).rgb; else {vec3 masks=texture2D(film,vec2((1.+uv.x)/3.,uv.y)).rgb; a=pass<.5?masks.r:masks.g; rgb=texture2D(film,vec2((uv.x+(pass<.5?0.:2.))/3.,uv.y)).rgb;}
  if(pass>=0.&&pass<.5)rgb=mix(rgb,vec3(dot(rgb,vec3(.2126,.7152,.0722))),stain)*tint; if(pass>.5)rgb=vec3(.12,.075,.043);
  rgb=mix(vec3(dot(rgb,vec3(.2126,.7152,.0722))),rgb,.83);rgb=clamp((rgb-.5)*1.035+.507,0.,1.);
  vec2 at=gl_FragCoord.xy/vec2(1120.,630.);float v=1.-.14*smoothstep(.2,.72,distance(at,vec2(.5)));rgb*=v;
  float grain=(fract(sin(dot(gl_FragCoord.xy+frame,vec2(12.9898,78.233)))*43758.5453)-.5)*.008;
  gl_FragColor=vec4(clamp(rgb+grain,0.,1.)*a,a);}`;
  const shader = (type: number, source: string) => { const item = gl.createShader(type)!; gl.shaderSource(item, source); gl.compileShader(item); if (!gl.getShaderParameter(item, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(item) ?? 'Shader failed'); return item; };
  const vs = shader(gl.VERTEX_SHADER, vertex), fs = shader(gl.FRAGMENT_SHADER, fragment), program = gl.createProgram()!;
  gl.attachShader(program, vs); gl.attachShader(program, fs); gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error('Compositor failed');
  gl.useProgram(program);
  const buffer = gl.createBuffer()!; gl.bindBuffer(gl.ARRAY_BUFFER, buffer); gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0,0,1,0,0,1,0,1,1,0,1,1]), gl.STATIC_DRAW);
  const position = gl.getAttribLocation(program, 'p'); gl.enableVertexAttribArray(position); gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
  const tint = gl.getUniformLocation(program, 'tint'), stain = gl.getUniformLocation(program, 'stain'), bounds = gl.getUniformLocation(program, 'bounds'), pass = gl.getUniformLocation(program, 'pass'), frameUniform = gl.getUniformLocation(program, 'frame');
  gl.uniform1i(gl.getUniformLocation(program, 'film'), 0); gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA); gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 0);
  const texture = () => { const item = gl.createTexture()!; gl.bindTexture(gl.TEXTURE_2D, item); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR); return item; };
  const backdrop = texture(); gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, plate); const textures = layers.map(texture);
  canvas.width = 1120; canvas.height = 630; gl.viewport(0, 0, 1120, 630);
  const drawTexture = (item: WebGLTexture, box: readonly number[], kind: number) => { gl.bindTexture(gl.TEXTURE_2D, item); gl.uniform4f(bounds, box[0], box[1], box[2], box[3]); gl.uniform1f(pass, kind); gl.drawArrays(gl.TRIANGLES, 0, 6); };
  return {
    draw(sources: readonly TexImageSource[], frame: number) {
      gl.useProgram(program); gl.uniform1f(frameUniform, frame); drawTexture(backdrop, [0,0,1,1], -1);
      sources.forEach((source, index) => { gl.bindTexture(gl.TEXTURE_2D, textures[index]); gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source); });
      layers.forEach((layer, index) => drawTexture(textures[index], layer.bounds, 1));
      layers.map((layer, index) => ({ layer, index })).sort((a,b) => (b.layer.depth[Math.min(27,frame)] ?? 0) - (a.layer.depth[Math.min(27,frame)] ?? 0)).forEach(({layer,index}) => { gl.uniform3f(tint, layer.tint[0], layer.tint[1], layer.tint[2]); gl.uniform1f(stain, layer.stain); drawTexture(textures[index], layer.bounds, 0); });
    },
    dispose() { textures.forEach((item) => gl.deleteTexture(item)); gl.deleteTexture(backdrop); gl.deleteBuffer(buffer); gl.deleteProgram(program); gl.deleteShader(vs); gl.deleteShader(fs); },
  };
}
